import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Project, MethodologyType, AnalysisMode, ResearchLens } from '@/types/database';
import { useToast } from '@/hooks/use-toast';
import { track } from '@/lib/analytics/mixpanel';
import { logEvent } from '@/lib/analytics/eventLog';

interface CreateProjectParams {
  name: string;
  description?: string;
  methodology: MethodologyType;
  research_aim?: string;
  rq1?: string;
  rq2?: string;
  rq3?: string;
  analysis_mode?: AnalysisMode;
  ignore_list?: string[];
  project_intent?: string;
}

const PROJECTS_QUERY_TIMEOUT_MS = 5000;

export function useProjects() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const projectsQuery = useQuery({
    queryKey: ['projects', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const cacheKey = `verithos_projects_cache_${user.id}`;
      const cachedProjects = (() => {
        try {
          const raw = localStorage.getItem(cacheKey);
          return raw ? (JSON.parse(raw) as Project[]) : null;
        } catch {
          return null;
        }
      })();

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), PROJECTS_QUERY_TIMEOUT_MS);

      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .abortSignal(controller.signal);

        if (error) throw error;

        const fresh = (data ?? []) as Project[];
        try {
          // Always overwrite cache so a wipe (empty list) doesn't get masked
          // by a stale cached array from before the reset.
          localStorage.setItem(cacheKey, JSON.stringify(fresh));
        } catch {
          // Non-blocking cache write
        }

        return fresh;
      } catch (error) {
        if (cachedProjects) {
          return cachedProjects;
        }

        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Projects request timed out.');
        }

        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    enabled: !!user,
    // Projects change rarely - keep fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  const createProject = useMutation({
    mutationFn: async (params: CreateProjectParams) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          name: params.name,
          description: params.description,
          methodology: params.methodology,
          research_aim: params.research_aim,
          rq1: params.rq1,
          rq2: params.rq2,
          rq3: params.rq3,
          analysis_mode: params.analysis_mode || 'hybrid',
          ignore_list: params.ignore_list,
          project_intent: params.project_intent,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Project;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      track('Project Created', { 
        project_name: project.name,
        has_research_lens: Boolean(project.research_aim),
      });
      logEvent('Project Created', { project_id: project.id });
      toast({
        title: 'Project created',
        description: 'Your new research project is ready.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to create project',
        description: error.message,
      });
    },
  });

  const updateProject = useMutation({
    mutationFn: async (params: Partial<Project> & { id: string }) => {
      if (!user) throw new Error('Not authenticated');
      
      const { id, name, description, methodology, research_aim, rq1, rq2, rq3, analysis_mode, ignore_list, archived_at, project_intent } = params;
      
      // Build update data - include all provided fields (including null values)
      const updateData: Record<string, unknown> = {
        // Always update updated_at to ensure the query returns a row
        updated_at: new Date().toISOString(),
      };
      
      if (name !== undefined) updateData.name = name;
      if (description !== undefined) updateData.description = description;
      if (methodology !== undefined) updateData.methodology = methodology;
      if ('research_aim' in params) updateData.research_aim = research_aim;
      if ('rq1' in params) updateData.rq1 = rq1;
      if ('rq2' in params) updateData.rq2 = rq2;
      if ('rq3' in params) updateData.rq3 = rq3;
      if ('analysis_mode' in params) updateData.analysis_mode = analysis_mode;
      if ('ignore_list' in params) updateData.ignore_list = ignore_list;
      if ('archived_at' in params) updateData.archived_at = archived_at;
      if ('project_intent' in params) updateData.project_intent = project_intent;
      
      // If focus fields are being updated, update focus_updated_at
      const focusFields = ['research_aim', 'rq1', 'rq2', 'rq3', 'analysis_mode', 'ignore_list'];
      const isFocusUpdate = focusFields.some(field => field in params);
      if (isFocusUpdate) {
        updateData.focus_updated_at = new Date().toISOString();
      }
      
      const { data, error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Project not found or you do not have permission to update it');
      return data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: 'Project updated',
        description: 'Your changes have been saved.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to update project',
        description: error.message,
      });
    },
  });

  const deleteProject = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: 'Project deleted',
        description: 'The project has been removed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to delete project',
        description: error.message,
      });
    },
  });

  return {
    projects: projectsQuery.data ?? [],
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error,
    createProject,
    updateProject,
    deleteProject,
  };
}
